import { useMemo } from 'react';
import {
  getDayEffectiveEndPosition,
  getImageUrl,
  groupDaysByExperience,
} from '@koob/utils';
import { CurrencyStateEnum } from '@koob/enums';
import { useTripDesignerConfig } from '../config/TripDesignerConfigProvider';
import useTripStore from '../store/useTripStore';
import { useDays, useTripFilters } from './useTripStoreSelectors';

/**
 * KD-3581 / US-035 — the view model of the experience frames of the
 * composer.
 *
 * `groupDaysByExperience` owns the grouping itself; this hook only joins
 * what each app keeps outside of the day (the experiences map, the extras,
 * the price the day already carries) and the factsheet URL, which
 * `config.getFactsheetUrl` already builds locale-aware and, on dmc, gated
 * on ownership. `days` is read, never rewritten.
 *
 * Indices are 0-based — the index in the `days` array, as everywhere past
 * `toTripState`. Amounts come out raw: the caller formats them next
 * to the trip currency, and i18n never enters this file.
 */

const formatPosition = (position: any) => {
  if (!position) {
    return null;
  }

  const city = position.city || position.name || position.title || '';
  const country = position.country ? `, ${position.country}` : '';

  return city || country ? `${city}${country}` : null;
};

// Same chain as useTripTotal: the price of an instance is carried by
// its first day, and `tripPrice`/`experiencePricing` already includes the
// additional services of the frame — no second source of truth here.
function resolveFramePricing(firstDay: any, experience: any) {
  const pricing =
    firstDay?.tripPrice ??
    firstDay?.experiencePricing ??
    experience?.tripPrice ??
    experience?.price;
  const amount = Number.parseFloat(pricing?.price);
  const currency = pricing?.currency;
  const hasPrice =
    Number.isFinite(amount) &&
    !!currency &&
    currency !== CurrencyStateEnum.MISMATCH;

  return { amount, currency, hasPrice };
}

// `days` is an optional override for a caller with its own view over the
// day list; both apps' consumers pass the store's own `useDays()` value
// today, which is exactly the default below.
export function useExperienceFrames(days?: any[]) {
  const { getFactsheetUrl } = useTripDesignerConfig();
  const storeDays = useDays();
  const effectiveDays = days ?? storeDays;
  const experiences = useTripStore((state) => state.experiences);
  const tripFilters = useTripFilters();
  const travellersCount = tripFilters?.composition?.count ?? 0;
  return useMemo(() => {
    const experiencesMap = experiences ?? {};
    return groupDaysByExperience({ days: effectiveDays }).map((frame: any) => {
      const experience = experiencesMap[frame.experienceId] ?? null;
      const firstDay = effectiveDays?.[frame.startDayIndex];
      const lastDay = effectiveDays?.[frame.endDayIndex];

      const firstProgram =
        experience?.programs?.[(firstDay?.experienceDayIndex ?? 1) - 1];
      const lastProgram =
        experience?.programs?.[(lastDay?.experienceDayIndex ?? 1) - 1];

      // KD-3548 — the frame ends where its last day actually ends: a
      // retained transfer moves it, and the header must not announce a
      // city the traveller has already left.
      const endPosition = getDayEffectiveEndPosition({
        days: effectiveDays,
        dayIndex: frame.endDayIndex,
        experiencesMap,
        experience,
        program: lastProgram,
      });

      const { amount, currency, hasPrice } = resolveFramePricing(
        firstDay,
        experience,
      );
      const startLocation = formatPosition(firstProgram?.startPosition);

      return {
        ...frame,
        experience,
        name: experience?.name ?? '',
        imageUrl: getImageUrl(experience),
        // Partner products are not consultable in detail: without a
        // factsheet the header shows the name alone, never a dead icon.
        factsheetUrl: getFactsheetUrl(experience) ?? undefined,
        startLocation,
        // A frame always announces where it ends, even when it ends
        // where it started (§9).
        endLocation: formatPosition(endPosition) ?? startLocation,
        price: hasPrice ? amount : null,
        currency: hasPrice ? currency : null,
        // `composition.count` is absent on a trip opened without its pax
        // split: the line is dropped rather than divided by zero.
        pricePerPerson:
          hasPrice && travellersCount > 0 ? amount / travellersCount : null,
      };
    });
  }, [effectiveDays, experiences, travellersCount, getFactsheetUrl]);
}
